import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, ShoppingBag, Star, Truck, ShieldCheck, Clock, Heart } from "lucide-react";

import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingButtons from "../components/FloatingButtons";
import ProductCard from "../components/ProductCard";
import QuickViewDialog from "../components/QuickViewDialog";
import { useProducts } from "../lib/ProductsContext";
import { useCart } from "../lib/CartContext";
import { optimizeImg } from "../lib/img";

export default function ProductDetail() {
  const { productId } = useParams();
  const { categories, products } = useProducts();
  const { addToCart } = useCart();
  const [quickView, setQuickView] = useState(null);
  const [favorite, setFavorite] = useState(false);
  const [activeImage, setActiveImage] = useState(0);

  const product = useMemo(() => products.find((p) => p.id === productId), [productId, products]);
  const gallery = useMemo(
    () => (product?.images?.length > 0 ? product.images : [product?.image].filter(Boolean)),
    [product]
  );
  const category = useMemo(
    () => (product ? categories.find((c) => c.id === product.cat) : null),
    [product, categories]
  );
  const related = useMemo(
    () =>
      product
        ? products.filter((p) => p.cat === product.cat && p.id !== product.id).slice(0, 4)
        : [],
    [product, products]
  );

  useEffect(() => { setActiveImage(0); }, [productId]);

  if (!product) {
    return (
      <div className="min-h-screen bg-white text-[#222222]" data-testid="product-not-found">
        <Header />
        <div className="frida-container py-32 text-center">
          <h1 className="font-serif text-3xl mb-4">Ürün bulunamadı</h1>
          <Link to="/" className="text-[#A85C34] font-medium hover:underline">
            Ana sayfaya dön
          </Link>
        </div>
        <Footer />
        <FloatingButtons />
      </div>
    );
  }

  const discountPercent = product.oldPrice
    ? Math.round(((product.oldPrice - product.price) / product.oldPrice) * 100)
    : 0;
  const outOfStock = product.stock === 0;

  return (
    <div className="min-h-screen bg-white text-[#222222]" data-testid="product-detail-page">
      <Header />

      <div className="frida-container pt-6">
        <nav className="flex items-center gap-2 text-xs text-[#888888]" data-testid="product-breadcrumb">
          <Link to="/" className="hover:text-[#A85C34]">Ana Sayfa</Link>
          <ChevronRight className="w-3 h-3" />
          {category && (
            <>
              <Link to={`/kategori/${category.id}`} className="hover:text-[#A85C34]">{category.name}</Link>
              <ChevronRight className="w-3 h-3" />
            </>
          )}
          <span className="text-[#222222]">{product.name}</span>
        </nav>
      </div>

      <section className="py-8 md:py-14">
        <div className="frida-container grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16">
          <div>
            <div className="relative aspect-square rounded-3xl overflow-hidden bg-[#F8F8F8]">
              <img
                src={optimizeImg(gallery[activeImage] || product.image, 800)}
                alt={product.name}
                width="800"
                height="800"
                decoding="async"
                className="w-full h-full object-cover"
              />
              {product.badges?.includes("indirim") && discountPercent > 0 && (
                <span className="absolute top-4 left-4 text-[10px] font-semibold tracking-[0.15em] uppercase px-3 py-1.5 rounded-full bg-[#D4A15C] text-white">
                  %{discountPercent}
                </span>
              )}
              {outOfStock && (
                <span className="absolute top-4 right-4 text-[10px] font-semibold tracking-[0.15em] uppercase px-3 py-1.5 rounded-full bg-[#888888] text-white">
                  Tükendi
                </span>
              )}
            </div>

            {gallery.length > 1 && (
              <div className="flex gap-3 mt-4" data-testid="product-detail-thumbnails">
                {gallery.map((img, i) => (
                  <button
                    key={img + i}
                    type="button"
                    onClick={() => setActiveImage(i)}
                    aria-label={`${product.name} görsel ${i + 1}`}
                    className={`w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden shrink-0 border-2 transition-colors ${
                      activeImage === i ? "border-[#A85C34]" : "border-transparent hover:border-[#EAEAEA]"
                    }`}
                    data-testid={`product-detail-thumbnail-${i}`}
                  >
                    <img
                      src={optimizeImg(img, 160)}
                      alt=""
                      width="80"
                      height="80"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col">
            {product.rating != null && (
              <div className="flex items-center gap-1 mb-3" data-testid="product-detail-rating">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-4 h-4 ${
                      i < Math.round(product.rating) ? "fill-[#D4A15C] text-[#D4A15C]" : "text-[#EAEAEA]"
                    }`}
                  />
                ))}
                <span className="text-xs text-[#888888] ml-1">({product.rating.toFixed(1)})</span>
              </div>
            )}

            <h1 className="font-serif text-3xl md:text-4xl text-[#222222] tracking-tight leading-tight mb-3" data-testid="product-detail-title">
              {product.name}
            </h1>

            <div className="flex items-baseline gap-3 mb-5">
              <span className="font-serif text-4xl text-[#A85C34] font-semibold" data-testid="product-detail-price">
                {product.price.toLocaleString("tr-TR")}₺
              </span>
              {product.oldPrice && (
                <span className="text-lg text-[#888888] line-through">
                  {product.oldPrice.toLocaleString("tr-TR")}₺
                </span>
              )}
            </div>

            <p className="text-[#555555] leading-relaxed mb-7">{product.desc}</p>

            <ul className="space-y-2.5 mb-8">
              {[
                { icon: Truck, label: "Aynı gün teslimat — Hatay, Adana, Osmaniye, Gaziantep · Türkiye geneli kargo" },
                { icon: ShieldCheck, label: "Taze çiçek garantisi" },
                { icon: Clock, label: "7/24 sipariş hattı" },
              ].map((b, i) => (
                <li key={i} className="flex items-center gap-3 text-sm text-[#555555]">
                  <b.icon className="w-4 h-4 text-[#A85C34]" />
                  {b.label}
                </li>
              ))}
            </ul>

            <div className="mt-auto flex gap-3">
              <button
                onClick={() => !outOfStock && addToCart(product)}
                disabled={outOfStock}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#A85C34] hover:bg-[#8B4726] text-white rounded-full px-6 py-4 font-medium transition-colors active:scale-95 disabled:bg-[#CCCCCC] disabled:cursor-not-allowed disabled:hover:bg-[#CCCCCC]"
                data-testid="product-detail-add-cart"
              >
                <ShoppingBag className="w-4 h-4" />
                {outOfStock ? "Tükendi" : "Sepete Ekle"}
              </button>
              <button
                onClick={() => setFavorite((f) => !f)}
                aria-label="Favorilere ekle"
                data-testid="product-detail-favorite"
                className="w-14 h-14 rounded-full border border-[#EAEAEA] hover:border-[#D4A15C] flex items-center justify-center transition-colors"
              >
                <Heart className={`w-4 h-4 ${favorite ? "fill-[#D4A15C] text-[#D4A15C]" : ""}`} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-16 md:py-20 bg-[#F8F8F8]">
          <div className="frida-container">
            <h2 className="font-serif text-2xl md:text-3xl text-[#222222] tracking-tight mb-8" data-testid="related-products-title">
              Bu Kategoriden <span className="italic text-[#A85C34]">Diğer Ürünler</span>
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-testid="related-products-grid">
              {related.map((p) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  onAddToCart={addToCart}
                  onQuickView={setQuickView}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
      <FloatingButtons />

      <QuickViewDialog
        product={quickView}
        onClose={() => setQuickView(null)}
        onAddToCart={addToCart}
      />
    </div>
  );
}
